import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import utc from 'dayjs/plugin/utc';
import { requestApiGetConjunctions } from '@/query/conjunctions/useQueryCdm';

dayjs.extend(utc);

interface DailySnapshot {
  urgentRiskCount: number;
  highRiskCount: number;
  totalMonitoredCount: number;
}

export interface DailyMetricsResult {
  today: DailySnapshot;
  yesterday: DailySnapshot;
}

export const fetchDailyMetrics = async (
  baseDate: Dayjs
): Promise<DailyMetricsResult> => {
  try {
    const today = baseDate.utc().startOf('day').subtract(1, 'day');
    const yesterday = today.subtract(1, 'day');

    const todayName = `SPACEMAP_CA_${today.format('YYYYMMMDD').toUpperCase()}T0000_S1`;
    const yesterdayName = `SPACEMAP_CA_${yesterday.format('YYYYMMMDD').toUpperCase()}T0000_S1`;

    const urgentParamsNum = {
      dcaUpper: 1,
      limit: 1,
      page: 0,
      tcaStart: dayjs(today)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(today)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };
    const highParamsNum = {
      dcaLower: 1,
      dcaUpper: 5,
      limit: 1,
      page: 0,
      tcaStart: dayjs(today)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(today)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };
    const totalParamsNum = {
      dcaUpper: 10,
      limit: 1,
      page: 0,
      tcaStart: dayjs(today)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(today)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };

    const urgentParamsNumYesterday = {
      dcaUpper: 1,
      limit: 1,
      page: 0,
      tcaStart: dayjs(yesterday)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(yesterday)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };
    const highParamsNumYesterday = {
      dcaLower: 1,
      dcaUpper: 5,
      limit: 1,
      page: 0,
      tcaStart: dayjs(yesterday)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(yesterday)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };
    const totalParamsNumYesterday = {
      dcaUpper: 10,
      limit: 1,
      page: 0,
      tcaStart: dayjs(yesterday)
        .utc()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      tcaEnd: dayjs(yesterday)
        .utc()
        .startOf('day')
        .add(3, 'day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      option: 'SIMPLE',
    };

    const promises = [
      requestApiGetConjunctions(urgentParamsNum, todayName),
      requestApiGetConjunctions(highParamsNum, todayName),
      requestApiGetConjunctions(totalParamsNum, todayName),

      requestApiGetConjunctions(urgentParamsNumYesterday, yesterdayName),
      requestApiGetConjunctions(highParamsNumYesterday, yesterdayName),
      requestApiGetConjunctions(totalParamsNumYesterday, yesterdayName),
    ];

    const results = await Promise.allSettled(promises);

    const getCount = (result: PromiseSettledResult<any>) => {
      if (result.status === 'fulfilled') {
        return result.value?.data?.totalCount ?? result.value?.totalCount ?? 0;
      }
      return 0;
    };

    return {
      today: {
        urgentRiskCount: getCount(results[0]),
        highRiskCount: getCount(results[1]),
        totalMonitoredCount: getCount(results[2]),
      },
      yesterday: {
        urgentRiskCount: getCount(results[3]),
        highRiskCount: getCount(results[4]),
        totalMonitoredCount: getCount(results[5]),
      },
    };
  } catch (error) {
    console.error('Error in fetchDailyMetrics:', error);
    throw error;
  }
};

const useDailyMetrics = (baseDate: Dayjs) => {
  const normalizedDateKey = baseDate.utc().startOf('day').format('YYYY-MM-DD');

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['daily-metrics', normalizedDateKey],
    queryFn: () => fetchDailyMetrics(baseDate),
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: (previousData) => previousData,
  });

  return {
    data: data ?? null,
    isLoading: isLoading || isFetching,
    error: (error as Error) ?? null,
  };
};

export default useDailyMetrics;
